/**
 * Token 存储。
 *
 * - access token 存内存：寿命短（默认 1h），刷新页面丢就 refresh 拿回来
 * - refresh token 存 localStorage：寿命长（默认 30 天），跨刷新 / 重开 tab 持久化
 *
 * XSS trade-off：localStorage 对 JS 可读，XSS 注入瞬间能拿走 refresh token 并离线持续 30 天利用。
 * 要收紧的两个调整点：缩短 JWT_REFRESH_TOKEN_EXPIRES，或把 refresh 也挪进内存（牺牲"刷新不掉线"）。
 */

const REFRESH_STORAGE_KEY = 'as.auth.refreshToken'

let accessToken: string | undefined
const listeners = new Set<() => void>()

export function getAccessToken(): string | undefined {
  return accessToken
}

export function setAccessToken(token: string | undefined): void {
  if (accessToken === token) return
  accessToken = token
  for (const fn of listeners) fn()
}

/**
 * 订阅 access token 变化（login / refresh / logout 触发）。
 * 主要给非 React 模块用（如 realtime 客户端：token 换了要重连）。
 */
export function subscribeAccessToken(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function getRefreshToken(): string | undefined {
  try {
    return localStorage.getItem(REFRESH_STORAGE_KEY) ?? undefined
  } catch {
    // localStorage 不可用（隐私模式 / 跨 origin iframe 等）→ 视为无 token
    return undefined
  }
}

export function setRefreshToken(token: string | undefined): void {
  try {
    if (token) localStorage.setItem(REFRESH_STORAGE_KEY, token)
    else localStorage.removeItem(REFRESH_STORAGE_KEY)
  } catch {
    // 写不进去就算了；下次启动当无 token 处理 → 重登
  }
}

export function clearTokens(): void {
  setAccessToken(undefined)
  setRefreshToken(undefined)
}
